import { DeleteOutlined, SearchOutlined } from '@ant-design/icons'
import { Button, Input, message, Popconfirm, Space } from 'antd'
import type { FilterDropdownProps } from 'antd/es/table/interface'
import { t } from 'i18next'
import type { Dispatch, ReactElement, SetStateAction } from 'react'

// eslint-disable-next-line @typescript-eslint/max-params
export function createDestroyColumn<T>(
  loading: boolean,
  setLoading: Dispatch<SetStateAction<boolean>>,
  onDestroy: (record: T) => Promise<void>,
  condition?: (x: T) => boolean,
  isSkipConfirm?: boolean,
) {
  return {
    title: '',
    key: 'destroy',
    width: 20,
    render(_text: unknown, record: T) {
      const confirm = async () => {
        try {
          setLoading(true)
          await onDestroy(record)
          void message.success(t(`削除しました`))
        } catch (error) {
          if (error instanceof Error) {
            void message.error(`${t(`削除に失敗しました。`)}${error.message}`)
          } else {
            void message.error(t(`削除に失敗しました。`))
          }
          throw error
        } finally {
          setLoading(false)
        }
      }

      if (condition !== undefined && !condition(record)) {
        return <></>
      }

      // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
      if (isSkipConfirm) {
        // eslint-disable-next-line @typescript-eslint/no-misused-promises,@typescript-eslint/strict-void-return
        return <Button type="text" icon={<DeleteOutlined />} onClick={confirm} />
      }

      return (
        <Popconfirm
          title={t(`本当に削除しますか?`)}
          // eslint-disable-next-line @typescript-eslint/no-misused-promises,@typescript-eslint/strict-void-return
          onConfirm={confirm}
          okText={t(`はい`)}
          cancelText={t(`いいえ`)}
          okButtonProps={{ loading }}
          onPopupClick={(e) => {
            e.stopPropagation()
          }}
        >
          <Button
            type="text"
            icon={<DeleteOutlined />}
            onClick={(e) => {
              e.stopPropagation()
            }}
          ></Button>
        </Popconfirm>
      )
    },
  }
}

const FilterDropdown = ({
  setSelectedKeys,
  selectedKeys,
  confirm,
  clearFilters,
}: FilterDropdownProps): ReactElement => (
  <div style={{ padding: 8 }}>
    <Input
      ref={(node) => {
        if (node !== null) {
          setTimeout(() => {
            node.focus()
          }, 100)
        }
      }}
      value={selectedKeys[0]}
      autoFocus={true}
      onChange={(e) => {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition,sonarjs/different-types-comparison
        setSelectedKeys(e.target.value === undefined ? [] : [e.target.value])
        confirm({ closeDropdown: false })
      }}
      onPressEnter={(e) => {
        confirm({ closeDropdown: true })
      }}
      style={{ width: 188, marginBottom: 8, display: 'block' }}
    />
    <Space>
      <Button
        onClick={(e) => {
          if (clearFilters !== undefined) {
            clearFilters()
          }
          confirm()
        }}
      >
        {t(`リセット`)}
      </Button>
    </Space>
  </div>
)

export function getColumnSearchProps<T>(recordToValue: (record: T) => string) {
  return {
    filterDropdown: FilterDropdown,
    filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
    onFilter: (value: unknown, record: T) =>
      recordToValue(record)
        .toLowerCase() // ?.は不要なはずだが、実装ミスでこれがありえるようなので追加
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        .includes(typeof value === 'string' ? value.toLowerCase() : (value as string)),
  }
}
